import type { Slide } from '@/framework/types';
import { frames } from './frames.generated';
import './orca.css';

/**
 * Five Lessons from Building an Open Managed Agents Runtime — The AI Conference,
 * San Francisco, 30 September 2026.
 *
 * The frames are raw HTML, injected rather than written as JSX. See README.md:
 * the brief was a pixel-exact port of the rendered deck, and every diagram label
 * depends on SVG attributes that hand-conversion loses.
 *
 * The HTML is a build-time constant generated from our own templates by
 * scratchpad/decks/port.py. Nothing here is user input, fetched at runtime, or
 * reachable from a URL, so the injection has no untrusted path into it. If this
 * deck ever needs to render content it did not author, that stops being true.
 */

type Meta = { section: string; notes: readonly string[] };

const META: readonly Meta[] = [
  {
    section: 'Title',
    notes: ['Name, company, and go. No throat-clearing — ten minutes, and people are eating.'],
  },
  {
    section: 'Who',
    notes: [
      'Who you are, then who we are — 30 seconds, no more.',
      'Created Pulsar and BookKeeper. StreamNative is the real-time data and agent infrastructure company; hundreds of enterprises run the data platform.',
      'Land the bridge: "Orca is the open agent platform we built on top of it, and its runtime is what this talk is about."',
    ],
  },
  {
    section: 'The premise',
    notes: [
      'Models and harnesses are commoditising. What does not commoditise is running someone else\'s agent inside your walls.',
    ],
  },
  {
    section: 'The problem',
    notes: [
      'Cost, control, choice — the three problems nobody demos.',
      'Same three drawings the site and the print flyer use, so the story is one story.',
      'Every demo shows the happy path. None shows the bill, the blast radius, or the lock-in.',
    ],
  },
  {
    section: 'The setup',
    notes: [
      'Five pieces everyone rebuilds, and how they fit: the model is the brain, the harness is the loop that drives it, the sandbox and durable state are the legs, the gateway is the one door out.',
      'Trace the shape with your hand — top, middle, two legs, door. Do not read the boxes aloud.',
      'Keep this short — it is paying for the introduction.',
    ],
  },
  {
    section: 'Lesson 1 · Build',
    notes: ['The harness is the product surface, not the model. Swap the model, keep the harness.'],
  },
  {
    section: 'Lesson 2 · Build',
    notes: ['Context belongs in the harness, not in the prompt. This is where Streamhouse pays off.'],
  },
  {
    section: 'Lesson 3 · Run',
    notes: ['If you cannot replay it, you cannot debug it. Durable state is not a nice-to-have.'],
  },
  {
    section: 'Lesson 4 · Govern',
    notes: [
      'A limit the agent can reach is not a limit. Enforce outside the agent, at the gateway.',
    ],
  },
  {
    section: 'Lesson 5 · Govern',
    notes: ['Credentials in a vault, budget at the gateway, every action on the record.'],
  },
  {
    section: 'Closing the loop',
    notes: ['Build, test, deploy, govern, monitor — the lifecycle, not a one-off deployment.'],
  },
  {
    section: 'Architecture',
    notes: [
      'Inside the engine — the same framing the site uses on the Agent Engine page.',
      'A registry holds what you declare. A harness server drives the turns, a sandbox isolates the tools, the gateway governs every call that leaves.',
      'Walk it left to right: SDK/CLI/UI, the engine, MCP and the LLM provider, and everything landing in your own event store.',
    ],
  },
  {
    section: 'The ask',
    notes: [
      'Developer preview: run it locally today at runorca.ai.',
      'For a hosted trial, point people at private preview — do not promise a date.',
      'AICONF1000 is a $1,000 StreamNative Cloud credit, good through 5 October. It is NOT free Orca hosting.',
    ],
  },
  {
    section: 'One more thing',
    notes: [
      'Twenty seconds, then stop.',
      'Data Streaming Summit, 7–8 October, San Francisco — the Data + Agent conference.',
      'The QR already has the discount applied. AICONF100 is the ticket discount — one character off AICONF1000, which is the cloud credit. Say the thing, not the code.',
    ],
  },
];

if (META.length !== frames.length) {
  throw new Error(
    `orca-five-lessons: ${frames.length} frames but ${META.length} metadata entries — re-run scratchpad/decks/port.py`,
  );
}

export const slides: readonly Slide[] = frames.map((html, i) => ({
  id: i + 1,
  section: META[i].section,
  theme: 'dark',
  notes: META[i].notes,
  render: () => (
    <div className="orca-stage" dangerouslySetInnerHTML={{ __html: html }} />
  ),
}));
